/**
 * Ingest (spec--pdfmarkuptool-image-markup 4.1, slice I1): every way a photo enters the app ends here.
 * Per file: sniff the magic bytes -> decode and downscale in one step (the browser applies EXIF orientation) ->
 * JPEG on ONE reused canvas -> sha256 -> `store.put`, durable before the next file starts.
 *
 * A single serial queue (concurrency 1, PR M11): iOS caps live canvas memory per page and the pdf.js canvases
 * already hold part of it, so files never decode in parallel, every bitmap is closed after its draw, and the
 * canvas is released (0 x 0) when the queue is idle. Two concurrent calls share the queue.
 *
 * Device evidence (iPadOS 18.3.1 probe, H:/working/_design-image/probe-reports) amends the spec text:
 * - Safari's createImageBitmap({imageOrientation: 'from-image'}) already returns an EXIF-6 photo upright, so
 *   nothing here rotates. Orientation is read only to SIZE the decode (which axis is the long edge).
 * - `lastModified` is the paste / drop / pick moment for most paths, so capturedAt comes from EXIF
 *   DateTimeOriginal first ({@link capturedAtOf}).
 * - A Google Images copy carries a `google.com/imgres` wrapper; {@link sourceOf} unwraps it.
 *
 * The DOM is reached only through {@link IngestPlatform}, so the queue and the steps are unit-tested with a fake;
 * the real decode / encode is tested in chromium (e2e/image-ingest.spec.ts).
 */
import type { MediaSourceKind } from '@bim-pdf/core';
import { exifCaptureMs, imageSize, isIngestible, readExif, sniffImage, type Size } from './sniff';
import { SHEET_PAPER } from '@/core/theme/colors';
import { sha256Hex } from '@/utils/sha256';

/** The asset address (spec 3.3): sha256 hex of the stored bytes. */
export { sha256Hex };

export type { Size };

/** MediaItem.source.kind (spec 3.2; core MEDIA_SOURCE_KINDS). */
export type SourceKind = MediaSourceKind;

export interface SourceHint {
    kind: SourceKind;
    /** The first http(s) `text/uri-list` entry, as copied (the adapters pick it; Ingest unwraps it). */
    url?: string;
    /** Used only for a Blob that is not a File (a File carries its own). */
    lastModified?: number;
}

/** The Asset's metadata (spec 3.3). */
export interface AssetMeta { id: string; mime: 'image/jpeg'; w: number; h: number; bytes: number }

/** MediaItem minus addedBy / addedAt / order (spec 3.2, 4.1). */
export interface IngestItem {
    asset: string;
    mime: 'image/jpeg';
    w: number;
    h: number;
    capturedAt?: number;
    source: { kind: SourceKind; url?: string };
}

export type IngestFailure = 'unsupported-type' | 'decode-failed' | 'quota' | 'too-large' | 'url-only';

/**
 * `imageUrl`: the image address a Google Images wrapper carried (`imgurl`), beside `url` = the page. MediaItem's
 * `source` has no field for it (spec 3.2), so it rides on the result for the caller (I6) and is not stored.
 */
export type IngestResult =
    | { ok: true; asset: AssetMeta; item: IngestItem; imageUrl?: string }
    | { ok: false; reason: IngestFailure; salvage?: Blob; url?: string; imageUrl?: string };

/** The part of the AssetStore (slice I2) Ingest needs. `put` is durable when it resolves, or rejects (quota). */
export interface IngestAssetStore {
    has(id: string): Promise<boolean>;
    put(rec: AssetMeta & { blob: Blob }): Promise<void>;
}

/** The long edge of the one stored tier (spec 3.3). */
export const LONG_EDGE = 2048;
/** The stepwise-canvas fallback never makes a canvas side above this (iPadOS 17 limit, PA s5 [52]). */
export const FALLBACK_CAP = 4096;
export const JPEG_QUALITY = 0.82;
/** PNG input (screenshots) keeps legible text (spec 3.3). */
export const PNG_QUALITY = 0.92;
/** The 3 MB hard cap `asset.promote` enforces (spec 6): a bigger encode could never leave the device. */
export const MAX_ASSET_BYTES = 3 * 1024 * 1024;
/** A header above this is refused before any decode (a 48 MP iPhone frame is ~49 MP). */
export const MAX_INPUT_PIXELS = 100_000_000;
/** A picked file's own date counts only when it is older than this at the ingest call (probe amendment 2). */
export const FILE_DATE_MIN_AGE_MS = 120_000;
/** Enough of the file for the sniff, the header size and the APP1 (<= 64 KB, first after SOI). */
const HEAD_BYTES = 256 * 1024;
const MAX_URL = 2048;

/**
 * Orientation the decode asks for. 'from-image' only: Safari / Chromium apply EXIF there, and nothing here
 * rotates again. (I1's fault injection flips this to 'none' -- the chromium orientation test goes red.)
 */
const DECODE_ORIENTATION: DecodeRequest['orientation'] = 'from-image';

// ---------------------------------------------------------------------------------------------------------------
// Pure rules

export function fitWithin(s: Size, max: number): Size {
    const long = Math.max(s.w, s.h);
    if (long <= max) return { w: s.w, h: s.h };
    const k = max / long;
    return { w: Math.max(1, Math.round(s.w * k)), h: Math.max(1, Math.round(s.h * k)) };
}

/** The displayed size of a stored frame: EXIF orientations 5-8 swap the axes. */
export function orientedSize(s: Size, orientation: number | undefined): Size {
    return orientation !== undefined && orientation >= 5 && orientation <= 8 ? { w: s.h, h: s.w } : { w: s.w, h: s.h };
}

/**
 * capturedAt (probe amendment 2): EXIF DateTimeOriginal of the ORIGINAL bytes; else, for a camera capture, its
 * lastModified (the capture moment there); else, for a picked file, its lastModified only when it is more than
 * 120 s before the ingest call (a camera-roll HEIC transcoded to JPEG gets the pick time); else absent. Paste and
 * drop dates are the paste / drop moment and never count.
 */
export function capturedAtOf(a: { kind: SourceKind; exifMs?: number; lastModified?: number; now: number }): number | undefined {
    if (a.exifMs !== undefined) return a.exifMs;
    if (a.lastModified === undefined || !(a.lastModified > 0)) return undefined;
    if (a.kind === 'camera') return a.lastModified;
    if (a.kind === 'file' && a.now - a.lastModified > FILE_DATE_MIN_AGE_MS) return a.lastModified;
    return undefined;
}

function httpUrl(s: string | null | undefined): string | undefined {
    if (!s || s.length > MAX_URL) return undefined;
    try {
        const u = new URL(s);
        return u.protocol === 'http:' || u.protocol === 'https:' ? s : undefined;
    } catch {
        return undefined;
    }
}

const GOOGLE_HOST = /^(www\.)?google\.[a-z]{2,3}(\.[a-z]{2})?$/;

/**
 * source.url from the copied URL (probe amendment 3): a Google Images `imgres` wrapper gives the page
 * (`imgrefurl`) as `url` and the image (`imgurl`) as `imageUrl`; any other http(s) URL as copied; anything
 * else (javascript:, data:, over 2048 chars) nothing.
 */
export function sourceOf(raw: string | undefined): { url?: string; imageUrl?: string } {
    const url = httpUrl(raw);
    if (!url) return {};
    const u = new URL(url);
    if (GOOGLE_HOST.test(u.hostname) && u.pathname === '/imgres') {
        const page = httpUrl(u.searchParams.get('imgrefurl'));
        const image = httpUrl(u.searchParams.get('imgurl'));
        if (page) return image ? { url: page, imageUrl: image } : { url: page };
        if (image) return { url: image };
    }
    return { url };
}

// ---------------------------------------------------------------------------------------------------------------
// The platform seam

export interface DecodeRequest { orientation: 'from-image' | 'none'; resize?: Size }

export interface DecodedBitmap { readonly width: number; readonly height: number; close(): void }

export interface IngestContext {
    fillStyle: unknown;
    fillRect(x: number, y: number, w: number, h: number): void;
    drawImage(src: never, dx: number, dy: number, dw: number, dh: number): void;
}

export interface IngestCanvas { width: number; height: number; getContext(id: '2d'): IngestContext | null }

export interface IngestPlatform {
    now(): number;
    /** createImageBitmap with the orientation, and resizeWidth / resizeHeight / resizeQuality 'high' when asked. */
    decode(blob: Blob, req: DecodeRequest): Promise<DecodedBitmap>;
    /** The ONE reused output canvas; called once per Ingest instance. */
    createCanvas(): IngestCanvas;
    /** Scratch canvases for the stepwise fallback only; absent -> the fallback draws the full frame directly. */
    createScratchCanvas?(w: number, h: number): IngestCanvas;
    toJpeg(canvas: IngestCanvas, quality: number): Promise<Blob>;
}

export function browserPlatform(): IngestPlatform {
    return {
        now: () => Date.now(),
        decode: (blob, req) => createImageBitmap(blob, {
            imageOrientation: req.orientation,
            ...(req.resize ? { resizeWidth: req.resize.w, resizeHeight: req.resize.h, resizeQuality: 'high' as const } : {}),
        }),
        createCanvas: () => document.createElement('canvas') as unknown as IngestCanvas,
        createScratchCanvas: (w, h) => {
            const c = document.createElement('canvas');
            c.width = w;
            c.height = h;
            return c as unknown as IngestCanvas;
        },
        toJpeg: (canvas, quality) => new Promise<Blob>((resolve, reject) => {
            (canvas as unknown as HTMLCanvasElement).toBlob(
                (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), 'image/jpeg', quality);
        }),
    };
}

// ---------------------------------------------------------------------------------------------------------------
// The queue

export type IngestFn = (
    files: Blob[], hint: SourceHint, store: IngestAssetStore, onProgress?: (done: number, total: number) => void,
) => Promise<IngestResult[]>;

class DecodeError extends Error {}

interface Drawable { source: unknown; close(): void }

const same = (a: Size, b: Size) => a.w === b.w && a.h === b.h;

/** One Ingest with its own queue and canvas. The app uses {@link ingest}; tests pass a fake platform. */
export function createIngest(platform: IngestPlatform): IngestFn {
    let canvas: IngestCanvas | null = null;
    let tail: Promise<unknown> = Promise.resolve();
    let pending = 0;

    const request = (resize?: Size): DecodeRequest => (resize ? { orientation: DECODE_ORIENTATION, resize } : { orientation: DECODE_ORIENTATION });

    /** The stepwise fallback: halve on scratch canvases (never a side above 4096) down to <= 2x the target. */
    function stepDown(full: DecodedBitmap, target: Size): Drawable {
        const make = platform.createScratchCanvas?.bind(platform);
        if (!make) return { source: full, close: () => full.close() };
        const scratch: IngestCanvas[] = [];
        let source: unknown = full;
        let fullOpen = true;
        let cur: Size = { w: full.width, h: full.height };
        try {
            for (;;) {
                let next: Size;
                if (Math.max(cur.w, cur.h) > FALLBACK_CAP) next = fitWithin(cur, FALLBACK_CAP);
                else if (cur.w >= target.w * 2 && cur.h >= target.h * 2) {
                    next = { w: Math.max(target.w, Math.round(cur.w / 2)), h: Math.max(target.h, Math.round(cur.h / 2)) };
                } else break;
                const c = make(next.w, next.h);
                scratch.push(c);
                const ctx = c.getContext('2d');
                if (!ctx) throw new DecodeError('no 2d context');
                ctx.drawImage(source as never, 0, 0, next.w, next.h);
                if (fullOpen) { full.close(); fullOpen = false; }
                source = c;
                cur = next;
            }
        } catch (e) {
            if (fullOpen) full.close();
            for (const c of scratch) { c.width = 0; c.height = 0; }
            throw e;
        }
        return {
            source,
            close: () => {
                if (fullOpen) { full.close(); fullOpen = false; }
                for (const c of scratch) { c.width = 0; c.height = 0; }
            },
        };
    }

    /** Decode + downscale in one step, sized from the header when it has one. */
    async function decodeFitted(file: Blob, stored: Size | null, orientation: number | undefined): Promise<{ src: Drawable; size: Size }> {
        const asIs = (b: DecodedBitmap) => ({ src: { source: b, close: () => b.close() }, size: { w: b.width, h: b.height } });
        let full = stored ? orientedSize(stored, orientation) : null;
        if (!full) {
            // No header size (HEIC): measure with a plain decode, keep it when it already fits.
            const measured = await platform.decode(file, request());
            full = { w: measured.width, h: measured.height };
            if (same(fitWithin(full, LONG_EDGE), full)) return asIs(measured);
            measured.close();
        }
        const target = fitWithin(full, LONG_EDGE);
        if (same(target, full)) return asIs(await platform.decode(file, request()));
        let bmp: DecodedBitmap;
        try {
            bmp = await platform.decode(file, request(target));
        } catch {
            // The resize options are unsupported (or the file is broken: the plain decode then throws too).
            const plain = await platform.decode(file, request());
            return { src: stepDown(plain, target), size: target };
        }
        if (target.w !== target.h && bmp.width === target.h && bmp.height === target.w) {
            // This browser resized in stored (pre-orientation) space: ask again with the axes swapped.
            bmp.close();
            bmp = await platform.decode(file, request({ w: target.h, h: target.w }));
        }
        return asIs(bmp);
    }

    async function encode(src: Drawable, size: Size, quality: number): Promise<Blob> {
        try {
            canvas ??= platform.createCanvas();
            canvas.width = size.w;
            canvas.height = size.h;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new DecodeError('no 2d context');
            // Alpha (PNG, GIF) is flattened on white; JPEG has none, so the fill is harmless there.
            ctx.fillStyle = SHEET_PAPER;
            ctx.fillRect(0, 0, size.w, size.h);
            ctx.drawImage(src.source as never, 0, 0, size.w, size.h);
        } finally {
            src.close();
        }
        return platform.toJpeg(canvas, quality);
    }

    async function one(file: Blob, hint: SourceHint, store: IngestAssetStore, now: number, source: { url?: string; imageUrl?: string }): Promise<IngestResult> {
        const fail = (reason: IngestFailure, salvage?: Blob): IngestResult => ({
            ok: false, reason, ...(salvage ? { salvage } : {}), ...source,
        });
        const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
        const kind = sniffImage(head);
        if (!isIngestible(kind)) return fail('unsupported-type');
        const stored = imageSize(head);
        if (stored && stored.w * stored.h > MAX_INPUT_PIXELS) return fail('too-large');
        const exif = kind === 'jpeg' ? readExif(head) : {};

        let decoded: { src: Drawable; size: Size };
        try {
            decoded = await decodeFitted(file, stored, exif.orientation);
        } catch {
            // HEIC decodes only where the browser can (Safari); elsewhere it is a type we do not take.
            return fail(kind === 'heic' ? 'unsupported-type' : 'decode-failed');
        }
        let jpeg: Blob;
        try {
            jpeg = await encode(decoded.src, decoded.size, kind === 'png' ? PNG_QUALITY : JPEG_QUALITY);
        } catch {
            return fail('decode-failed');
        }
        if (jpeg.size > MAX_ASSET_BYTES) return fail('too-large');

        const asset: AssetMeta = { id: await sha256Hex(jpeg), mime: 'image/jpeg', w: decoded.size.w, h: decoded.size.h, bytes: jpeg.size };
        try {
            // Same encoded bytes -> same address: already stored is already durable.
            if (!(await store.has(asset.id))) await store.put({ ...asset, blob: jpeg });
        } catch {
            // QuotaError (I2) -- or any other refusal: the original goes back to the caller to offer as a
            // download, so a camera shot is never lost (M1).
            return fail('quota', file);
        }
        const capturedAt = capturedAtOf({
            kind: hint.kind,
            exifMs: kind === 'jpeg' ? exifCaptureMs(head) : undefined,
            lastModified: file instanceof File ? file.lastModified : hint.lastModified,
            now,
        });
        const item: IngestItem = {
            asset: asset.id, mime: 'image/jpeg', w: asset.w, h: asset.h,
            ...(capturedAt !== undefined ? { capturedAt } : {}),
            source: { kind: hint.kind, ...(source.url ? { url: source.url } : {}) },
        };
        return { ok: true, asset, item, ...(source.imageUrl ? { imageUrl: source.imageUrl } : {}) };
    }

    async function batch(files: Blob[], hint: SourceHint, store: IngestAssetStore, now: number,
        onProgress?: (done: number, total: number) => void): Promise<IngestResult[]> {
        const source = sourceOf(hint.url);
        if (files.length === 0) {
            // A copy that carried only a URL (WebP / AVIF / HEIC from Safari, an HDR screenshot; IP s1a, s1c).
            return source.url ? [{ ok: false, reason: 'url-only', ...source }] : [];
        }
        const out: IngestResult[] = [];
        for (const f of files) {
            out.push(await one(f, hint, store, now, source));
            onProgress?.(out.length, files.length);
        }
        return out;
    }

    return (files, hint, store, onProgress) => {
        const now = platform.now();
        pending++;
        const run = tail.then(() => batch(files, hint, store, now, onProgress));
        tail = run.catch(() => undefined);
        return run.finally(() => {
            pending--;
            if (pending === 0 && canvas) {
                canvas.width = 0;
                canvas.height = 0;
            }
        });
    };
}

let appIngest: IngestFn | null = null;
let appBatches = 0;

/** The app's Ingest: one queue and one canvas for the page (spec 4.1). */
export const ingest: IngestFn = (files, hint, store, onProgress) => {
    appIngest ??= createIngest(browserPlatform());
    appBatches++;
    const done = appIngest(files, hint, store, onProgress);
    void done.then(() => appBatches--, () => appBatches--);
    return done;
};

/** Whether the app's Ingest has a batch queued or running (the start-up eviction waits for it, spec 4.2). */
export function ingestRunning(): boolean {
    return appBatches > 0;
}
